import CircleWithArrowIcon from "@/assets/colorable/circle_with_arrow.svg?react";
import TimesIcon from "@/assets/colorable/times.svg?react";
import BoostVideoButton from "@/components/Campaign/management/components/BoostVideoButton";
import CarouselHeader from "@/components/Campaign/management/components/carousel/CarouselHeader";
import CarouselItem from "@/components/Campaign/management/components/carousel/CarouselItem";
import CarouselFooter from "@/components/Campaign/management/components/carousel/footer/CarouselFooter";
import {useSourceAlbum} from "@/hooks/useSourceAlbum";
import clsxm from "@/lib/clsxm";
import {useAdminCarouselStore, useCarouselStore} from "@/store/carousel";
import {isAdminFeedbackModalOpenAtom} from "@/store/jotaiAtoms";
import {ModalContext} from "@/store/modalContext";
import {useAtomValue} from "jotai";
import last from "lodash/last";
import {FC, useContext, useEffect, useId, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Portal} from "react-portal";
import Slider, {Settings} from "react-slick";
import {useKeyPressEvent, useMedia} from "react-use";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";

const config: Settings = {
  dots: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
};

const Carousel: FC = () => {
  const {t} = useTranslation("media_library");
  const isWide = useMedia("(min-width: 1024px)");
  const sliderRef = useRef<Slider>(null);

  const isAdminFeedbackModalOpen = useAtomValue(isAdminFeedbackModalOpenAtom);
  const carouselStore = isAdminFeedbackModalOpen ? useAdminCarouselStore : useCarouselStore;

  const id = useId();
  const {modalIds, setModalId, deleteModalId} = useContext(ModalContext);
  useEffect(() => {
    setModalId(id);

    return () => {
      deleteModalId(id);
    };
  }, []);
  const isLastModal = last(modalIds) === id;
  const currentMedia = carouselStore(state => state.currentMedia);
  const mediaLength = carouselStore(state => state.mediaLength);
  const onChange = carouselStore(state => state.onChange);
  const close = carouselStore(state => state.reset);
  useKeyPressEvent("Escape", () => {
    if (isLastModal) close();
  });
  useKeyPressEvent("ArrowLeft", () => {
    if (isLastModal) sliderRef.current?.slickPrev();
  });
  useKeyPressEvent("ArrowRight", () => {
    if (isLastModal) sliderRef.current?.slickNext();
  });
  const initialMediaIndex = carouselStore(state => state.initialMediaIndex);
  const shouldLoop = carouselStore(state => state.shouldLoop);
  const carouselCampaign = carouselStore(state => state.campaign);
  const shouldUsePersistedIndex = carouselStore(state => state.shouldUsePersistedIndex);
  const persistedCurrentIdx = carouselStore(state => state.currentIndex);
  const showFooter = carouselStore(state => state.showFooter);
  const staticFooter = carouselStore(state => state.staticFooter);
  const setPersistedCurrentIdx = carouselStore(state => state.setCurrentIndex);
  const [currentIdx, setCurrentIdx] = useState(initialMediaIndex);
  const currentMediaIndex = shouldUsePersistedIndex ? persistedCurrentIdx : currentIdx;
  const setCurrentMediaIndex = shouldUsePersistedIndex ? setPersistedCurrentIdx : setCurrentIdx;
  const hasPrevious = shouldLoop || currentMediaIndex > 0;
  const hasNext = shouldLoop || currentMediaIndex < currentMedia.length - 1;
  const currentMediaInstagramMedia = currentMedia[currentMediaIndex]?.instagram_media;
  const visibleMedia = useSourceAlbum({content: currentMedia[currentMediaIndex]});

  // A tap that reflows the content under the finger - selecting an album thumbnail swaps the media
  // shown above it - releases on a different element than it pressed, and the browser then dispatches
  // the click on those two elements' common ancestor. That ancestor sits above the handlers that stop
  // propagation, so the click reaches this backdrop and would close the carousel. A deliberate
  // backdrop click presses and releases on the same element, so require exactly that.
  const pressedElement = useRef<EventTarget | null>(null);

  return (
    <Portal node={document && document.getElementById("root")}>
      <div
        onPointerDown={e => (pressedElement.current = e.target)}
        onClick={e => {
          if (e.target === pressedElement.current) close();
        }}
        style={{
          zIndex: 9999 + modalIds.findIndex(modalId => modalId === id),
        }}
        className="fixed top-1/2 left-1/2 flex h-full w-full -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden bg-black/90 shadow-lg">
        <div
          className="relative mb-4 flex min-h-12 shrink-0 items-center bg-black px-5 py-2.5"
          onClick={e => e.stopPropagation()}>
          {currentMedia[currentMediaIndex] && (
            <CarouselHeader
              currentMedia={currentMedia[currentMediaIndex]}
              currentMediaIndex={currentMediaIndex}
              mediaLength={mediaLength ?? currentMedia.length}
            />
          )}
          {!!currentMediaInstagramMedia?.video_code && (
            <BoostVideoButton
              buttonStyle="Custom"
              campaignId={currentMedia[currentMediaIndex].campaign?.id ?? carouselCampaign?.id}
              className="mr-10 ml-auto h-8 w-fit shrink-0 gap-1.5 rounded-md border border-white/15 bg-white/10 px-3 text-sm font-medium whitespace-nowrap text-white enabled:hover:bg-white/15"
              contentType={currentMediaInstagramMedia.media_product_type}
              iconClassName="text-white"
              id={currentMedia[currentMediaIndex].id}
              label={t("copy_ad_code")}
              lastCopied={currentMediaInstagramMedia.last_copied}
              shouldKeepStyleOnSuccess
              videoCode={currentMediaInstagramMedia.video_code}
            />
          )}
          <button onClick={close} className="absolute top-1/2 right-5 z-10 -translate-y-1/2">
            <TimesIcon className="text-neutral-grey-light hover:text-neutral-grey-dark size-5 transition-colors duration-150" />
          </button>
        </div>
        <div className="relative flex min-h-0 flex-1">
          {currentMedia.length > 1 && hasPrevious && (
            <button
              onClick={e => {
                e.stopPropagation();
                sliderRef.current?.slickPrev();
              }}
              className={"absolute top-1/2 z-20 " + (isWide ? "left-40" : "left-4")}>
              <CircleWithArrowIcon className={clsxm("-rotate-180 text-white", isWide ? "size-16" : "size-8")} />
            </button>
          )}
          <div className="flex w-full justify-center overflow-x-hidden overflow-y-auto sm:px-8 sm:pb-8">
            <div className="flex min-h-full w-full flex-col items-center sm:justify-center">
              <div className="flex w-full flex-col items-center max-sm:my-auto">
                <Slider
                  // NOTE: afterChange is broken when adaptiveHeight is set to true. See:
                  // https://github.com/akiran/react-slick/issues/1262. Therefore this hacky solution.
                  beforeChange={(_, next) => {
                    onChange?.(next);
                    setCurrentMediaIndex(next);
                  }}
                  className="w-full max-sm:px-14 sm:w-[60vw] sm:max-w-[70%]"
                  arrows={false}
                  adaptiveHeight={staticFooter}
                  {...config}
                  infinite={shouldLoop}
                  ref={sliderRef}
                  initialSlide={initialMediaIndex}>
                  {currentMedia.map((content, index) => (
                    <CarouselItem key={index} content={content} isCurrentlyVisible={index === currentMediaIndex} />
                  ))}
                </Slider>
                {staticFooter && showFooter && visibleMedia && (
                  <div className="mt-2 max-sm:w-full max-sm:px-2 max-sm:pb-2">
                    <CarouselFooter content={visibleMedia} />
                  </div>
                )}
              </div>
            </div>
          </div>
          {currentMedia.length > 1 && hasNext && (
            <button
              onClick={e => {
                e.stopPropagation();
                sliderRef.current?.slickNext();
              }}
              className={"absolute top-1/2 z-20 " + (isWide ? "right-40" : "right-4")}>
              <CircleWithArrowIcon className={clsxm("text-white", isWide ? "size-16" : "size-8")} />
            </button>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default Carousel;
